import axios from 'axios';
import authHeader from '../auth-header';
import { getUrl } from '../service-utils';

const API_URL = getUrl();

/**
 * Get a specific itinerary from a trip
 * @param tripId - The ID of the trip
 * @param itineraryId - The ID of the itinerary to get
 */
export const getItinerary = async (tripId: string, itineraryId: string) => {
  const response = await axios.get(
    `${API_URL}trips/${tripId}/itineraries/${itineraryId}`,
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Create a new itinerary from trip activities (only requires tripId)
 * @param tripId - The ID of the trip
 */
export const createItinerary = async (tripId: string) => {
  const response = await axios.post(
    `${API_URL}trips/${tripId}/itineraries/create`,
    {},
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Create a new trip itinerary with custom data
 * @param tripId - The ID of the trip
 * @param itineraryData - The itinerary data to create
 */
export const createTripItinerary = async (
  tripId: string,
  itineraryData: any
) => {
  const response = await axios.post(
    `${API_URL}trips/${tripId}/itineraries/`,
    itineraryData,
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Copy an existing itinerary within a trip
 * @param tripId - The ID of the trip
 * @param itineraryId - The ID of the itinerary to copy
 */
export const copyItinerary = async (tripId: string, itineraryId: string) => {
  const response = await axios.post(
    `${API_URL}trips/${tripId}/itineraries/${itineraryId}/copy`,
    {},
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Update a trip itinerary structure (activity reordering)
 * @param tripId - The ID of the trip
 * @param itineraryId - The ID of the itinerary to update
 * @param updatedItinerary - The updated itinerary structure
 */
export const updateTripItineraryStructure = async (
  tripId: string,
  itineraryId: string,
  updatedItinerary: any
) => {
  const response = await axios.put(
    `${API_URL}trips/${tripId}/itineraries/${itineraryId}`,
    updatedItinerary,
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Reorder an activity within an itinerary day
 * @param tripId - The ID of the trip
 * @param itineraryId - The ID of the itinerary
 * @param activityId - The ID of the activity to reorder
 * @param newIndex - The new index position for the activity
 */
export const reorderActivity = async (
  tripId: string,
  itineraryId: string,
  activityId: string,
  newIndex: number
) => {
  const response = await axios.patch(
    `${API_URL}trips/${tripId}/itineraries/${itineraryId}/activities/${activityId}/reorder`,
    { newIndex },
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Delete an itinerary from a trip
 * @param tripId - The ID of the trip
 * @param itineraryId - The ID of the itinerary to delete
 */
export const deleteItinerary = async (tripId: string, itineraryId: string) => {
  const response = await axios.delete(
    `${API_URL}trips/${tripId}/itineraries/${itineraryId}`,
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Move a day from one itinerary to another
 * @param tripId - The ID of the trip
 * @param sourceItineraryId - The ID of the source itinerary
 * @param dayNumber - The day number to move
 * @param targetItineraryId - The ID of the target itinerary
 */
export const moveDayBetweenItineraries = async (
  tripId: string,
  sourceItineraryId: string,
  dayNumber: number,
  targetItineraryId: string
) => {
  const response = await axios.post(
    `${API_URL}trips/${tripId}/itineraries/${sourceItineraryId}/days/${dayNumber}/move`,
    { targetItineraryId },
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Copy a day from one itinerary to another
 * @param tripId - The ID of the trip
 * @param sourceItineraryId - The ID of the source itinerary
 * @param dayNumber - The day number to copy
 * @param targetItineraryId - The ID of the target itinerary
 * @param newDayName - Optional new name for the copied day
 */
export const copyDayBetweenItineraries = async (
  tripId: string,
  sourceItineraryId: string,
  dayNumber: number,
  targetItineraryId: string,
  newDayName?: string
) => {
  const response = await axios.post(
    `${API_URL}trips/${tripId}/itineraries/${sourceItineraryId}/days/${dayNumber}/copy`,
    { targetItineraryId, newDayName },
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Move an activity from one day to another within the same itinerary
 * @param tripId - The ID of the trip
 * @param itineraryId - The ID of the itinerary
 * @param sourceDayNumber - The source day number
 * @param targetDayNumber - The target day number
 * @param activityId - The ID of the activity to move
 */
export const moveActivityBetweenDays = async (
  tripId: string,
  itineraryId: string,
  sourceDayNumber: number,
  targetDayNumber: number,
  activityId: string
) => {
  const response = await axios.post(
    `${API_URL}trips/${tripId}/itineraries/${itineraryId}/days/${sourceDayNumber}/activities/${activityId}/move`,
    { targetDayNumber },
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Copy an activity from one day to another within the same itinerary
 * @param tripId - The ID of the trip
 * @param itineraryId - The ID of the itinerary
 * @param sourceDayNumber - The source day number
 * @param targetDayNumber - The target day number
 * @param activityId - The ID of the activity to copy
 * @param newActivityName - Optional new name for the copied activity
 */
export const copyActivityBetweenDays = async (
  tripId: string,
  itineraryId: string,
  sourceDayNumber: number,
  targetDayNumber: number,
  activityId: string,
  newActivityName?: string
) => {
  const response = await axios.post(
    `${API_URL}trips/${tripId}/itineraries/${itineraryId}/days/${sourceDayNumber}/activities/${activityId}/copy`,
    { targetDayNumber, newActivityName },
    {
      headers: authHeader()
    }
  );
  return response.data;
};

/**
 * Add a trip activity to an itinerary day, identified by day number.
 * The server will automatically create any missing days from 1 to dayNumber
 * before adding the activity, so no prior day-creation call is needed.
 * @param tripId - The ID of the trip
 * @param itineraryId - The ID of the itinerary
 * @param dayNumber - The 1-based day number to add the activity to
 * @param activityData - The activity data to add
 */
export const addActivityToDay = async (
  tripId: string,
  itineraryId: string,
  dayNumber: number,
  activityData: any
) => {
  const response = await axios.post(
    `${API_URL}trips/${tripId}/itineraries/${itineraryId}/days/by-day-number/${dayNumber}/activities`,
    activityData,
    {
      headers: authHeader()
    }
  );
  return response.data;
};
